import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import DotLegalDeleteDialogWrapper from "../../common/components/dotLegalDeleteDialog/DotLegalDeleteDialogWrapper";
import { useDotLegalQueryClient } from "../../common/hooks/dotLegalReactQuery";
import { useTranslation } from "../../localization/useTranslation";
import { AiChatConversationListItem } from "../AiChat.types";
import { aiChatConversationsQueryKey } from "../sidebar/AiChatSidebar.hooks";
import { useDeleteAiChatConversationMutation } from "./DeleteAiChatConversationDialog.hooks";

interface DeleteAiChatConversationDialogProps {
    conversation: AiChatConversationListItem;
    activeConversationId: string | null;
    onDialogClose: () => void;
    onActiveConversationDeleted: () => void;
}

function DeleteAiChatConversationDialog({
    conversation,
    activeConversationId,
    onDialogClose,
    onActiveConversationDeleted,
}: DeleteAiChatConversationDialogProps) {
    const { translateString } = useTranslation();
    const queryClient = useDotLegalQueryClient();
    const snackbar = useDotLegalSnackbar();
    const deleteMutation = useDeleteAiChatConversationMutation();

    async function handleConfirm() {
        const wasActive = conversation.id === activeConversationId;
        const idToDelete = conversation.id;
        onDialogClose();

        await queryClient.cancelQueries(aiChatConversationsQueryKey);
        const previousConversations = queryClient.getQueryData<AiChatConversationListItem[]>(aiChatConversationsQueryKey);
        queryClient.setQueryData<AiChatConversationListItem[]>(aiChatConversationsQueryKey, (prev) => (prev ?? []).filter((c) => c.id !== idToDelete));
        if (wasActive) onActiveConversationDeleted();

        try {
            await deleteMutation.mutateAsync(idToDelete);
        } catch {
            // Restore the row so the user can navigate back, but leave them in the fresh chat
            // they've already started — yanking them back would be jarring.
            if (previousConversations) {
                queryClient.setQueryData<AiChatConversationListItem[]>(aiChatConversationsQueryKey, previousConversations);
            }
            snackbar.show(translateString("aiChatConversationDeleteFailed"), "error");
        }
    }

    const itemForDeletion = conversation.title.trim() || translateString("aiChatUntitledConversation");
    return <DotLegalDeleteDialogWrapper open itemForDeletion={itemForDeletion} onDialogClose={onDialogClose} onOkClick={handleConfirm} />;
}

export default DeleteAiChatConversationDialog;
